import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme } from './theme/themeStore';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Custom Components
import { Login } from './components/Login';
import { Layout } from './components/Layout';
import { Dashboard } from './components/Dashboard/Dashboard';
import { ReleaseManagement } from './components/ReleaseManager/ReleaseManagement';
import { FleetConsole } from './components/FleetConsole';
import { CompanySelectorModal } from './components/CompanySelectorModal';
import { ConsoleUserRegistry } from './components/ConsoleUserRegistry';

function App() {
  // Initialize auth state based on existing token
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  
  // Organization State Logic
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const saved = localStorage.getItem('last_selected_org');
    return saved ? JSON.parse(saved) : { 
        cid: 1000, 
        id: 1000, 
        name: 'Demo', 
        logo: 'im_dreamtecsoftware.png', 
        customer_type: 'FUEL' 
    };
  });

  /**
   * CENTRAL LOGOUT LOGIC
   * Clears all sensitive data and forces redirect to /login
   */
  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('last_selected_org');
    setIsAuthenticated(false);
  }, []);

  /**
   * GLOBAL AUTH INTERCEPTOR
   * Listens for 'auth-unauthorized' events dispatched by child components
   * when they receive a 401 error from the API.
   */
  useEffect(() => {
    const handleAuthError = () => {
        console.warn("Session expired or unauthorized. Logging out...");
        handleLogout();
    };

    window.addEventListener('auth-unauthorized', handleAuthError);
    return () => window.removeEventListener('auth-unauthorized', handleAuthError);
  }, [handleLogout]);

  /**
   * SESSION HEARTBEAT & AUTO-EXTEND
   * Periodically validates the token and refreshes it.
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        handleLogout();
        return;
      }

      try {
        const response = await fetch('https://console.dreamtec.io/console-users/me', {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // If server issues a new token in the response, save it to extend the session
          if (data.access_token) {
            localStorage.setItem('token', data.access_token);
            console.log("Session heartbeat: Token refreshed.");
          }
        } else if (response.status === 401) {
          handleLogout();
        }
      } catch (err) {
        console.error("Session heartbeat failed to connect to server:", err);
      }
    };

    // Initial check on mount/login
    checkSession();

    // Check every 15 minutes
    const heartbeatInterval = setInterval(checkSession, 15 * 60 * 1000); 

    return () => clearInterval(heartbeatInterval);
  }, [isAuthenticated, handleLogout]);

  const handleSelectOrg = (org) => {
    setSelectedOrg(org);
    localStorage.setItem('last_selected_org', JSON.stringify(org));
    setIsSelectorOpen(false);
  };

  return (
    <Router basename="/manage">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          {/* Public Route: Login */}
          <Route 
            path="/login" 
            element={!isAuthenticated ? <Login onLogin={() => setIsAuthenticated(true)} /> : <Navigate to="/" />} 
          />
          
          {/* Protected Routes: Wrapped in Layout */}
          <Route element={
            isAuthenticated ? (
              <Layout 
                onLogout={handleLogout} 
                selectedOrg={selectedOrg} 
                onOpenSelector={() => setIsSelectorOpen(true)} 
              />
            ) : <Navigate to="/login" />
          }>
            
            {/* Dashboard: System Intelligence */}
            <Route path="/" element={<Dashboard />} />
            
            {/* Releases: APK & PCG Management */}
            <Route path="/releases" element={<ReleaseManagement />} />
            
            {/* Users: Console Admin Management */}
            <Route path="/users" element={<ConsoleUserRegistry />} />
            
            {/* Fleet: SOTI-Synced Hardware Registry */}
            <Route 
              path="/fleet/:feature" 
              element={
                <FleetConsole 
                    key={selectedOrg?.cid || selectedOrg?.id} 
                    selectedOrg={selectedOrg} 
                />
              } 
            />
            
            {/* Fallback for fleet base path */}
            <Route path="/fleet" element={<Navigate to="/fleet/gateways" />} />
          </Route>

          {/* Catch-all: Redirect to Home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        {/* Global Organization Selector */}
        <CompanySelectorModal 
          open={isSelectorOpen} 
          onClose={() => setIsSelectorOpen(false)} 
          onSelect={handleSelectOrg}
          currentId={selectedOrg?.cid || selectedOrg?.id}
        />
      </ThemeProvider>
    </Router>
  );
}

export default App;